import React, { useEffect, useRef, useState } from 'react';
import * as fabric from 'fabric';
import axios from 'axios';
import VisitHistoryModal from './VisitHistoryModal';
import CollapsiblePatientInfo from './CollapsiblePatientInfo';
import PortalGate from './portal/PortalGate';
import DoctorDashboard from './portal/DoctorDashboard';
import FrontDeskDashboard from './portal/FrontDeskDashboard';
import { jsPDF } from "jspdf"; 
import { 
    LuPenLine, LuHand, LuEraser, LuSave, LuImage, LuFileText, LuX, LuEllipsisVertical 
} from 'react-icons/lu';

function App() {
    const canvasRef = useRef(null);
    const fabricCanvasRef = useRef(null);
    const canvasContainerRef = useRef(null);
    
    // --- REFS ---
    const isHandToolRef = useRef(false);
    const isReadOnlyRef = useRef(false);
    const isEraserRef = useRef(false);
    const penColorRef = useRef('#000000');
    const penSizeRef = useRef(0.7);
    const isCanvasLockedRef = useRef(false);

    const [lastSaved, setLastSaved] = useState(null);
    const [isEraser, setIsEraser] = useState(false);
    const [eraserSize, setEraserSize] = useState(10); 
    const [isHandTool, setIsHandTool] = useState(false); 
    
    const [penColor, setPenColor] = useState('#000000');
    const [penSize, setPenSize] = useState(0.7);
    
    const [zoomLevel, setZoomLevel] = useState(1);
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showTemplates, setShowTemplates] = useState(false);
    
    const [showVisitHistory, setShowVisitHistory] = useState(false);
    const [currentVisit, setCurrentVisit] = useState(null);
    const searchParams = new URLSearchParams(window.location.search);
    const isUrlReadOnly = searchParams.get('mode') === 'history' || searchParams.get('readonly') === 'true';
    const [isVisitLocked, setIsVisitLocked] = useState(false);
    const isReadOnly = isUrlReadOnly || isVisitLocked;
    
    // PDF View State
    const [isPdfView, setIsPdfView] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    
    const [showFileOps, setShowFileOps] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false); 
    
    const [isPanning, setIsPanning] = useState(false);
    const [isPatientSaved, setIsPatientSaved] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const formatToThaiBE = (dateStr) => {
    if (!dateStr || dateStr.trim() === "" || dateStr === "null" || dateStr === "-") return "-";

};
    
    const [patientContext, setPatientContext] = useState({
        centralHN: null, branchHN: null, name: null, branch: null,
        branchCode: null, doctor: null, appointmentDate: null, 
        mode: null, validationError: null,
        x_studio_personal_illness: null, x_studio_drug_allergies: null,
        x_studio_food_allergies: null, x_studio_age: null,
        x_studio_birthdate: null, x_studio_career: null,
        x_studio_email: null, x_studio_source_1: null,
        x_studio_weight: null, x_studio_height: null
    });
    


    // Role/Access States
    const [userRole, setUserRole] = useState(localStorage.getItem('user_role') || 'doctor');
    const [doctorName, setDoctorName] = useState(localStorage.getItem('doctor_name') || '');
    const [userBranch, setUserBranch] = useState(localStorage.getItem('user_branch') || '');
    
    const params = new URLSearchParams(window.location.search);
    const isFromOdoo = params.get('branch_hn');
    const CONFIRM_LOCK_FLAG = "VISIT_CONFIRMED_LOCKED";
    const [lockMessage, setLockMessage] = useState('');

    // --- SYNC REFS ---
    useEffect(() => { isHandToolRef.current = isHandTool; }, [isHandTool]);
    useEffect(() => { isReadOnlyRef.current = isReadOnly; }, [isReadOnly]);
    useEffect(() => { isEraserRef.current = isEraser; }, [isEraser]);
    useEffect(() => { penColorRef.current = penColor; }, [penColor]);
    useEffect(() => { penSizeRef.current = penSize; }, [penSize]);

    // --- [INITIAL DATA LOADING] ---
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        
        const branchHN = params.get('branch_hn');
        const name = params.get('name');
        const branch = params.get('branch');
        const branchCode = params.get('branch_code') || extractBranchCode(branchHN, branch);
        const doctor = params.get('doctor');
        const centralHN = params.get('hn') || params.get('central_hn');
        const mode = params.get('mode');
        const readonlyFlag = params.get('readonly');
        const appointmentDate = params.get('appointment_date'); 
        
        const x_studio_personal_illness = params.get('x_studio_personal_illness');
        const x_studio_drug_allergies = params.get('x_studio_drug_allergies');
        const x_studio_food_allergies = params.get('x_studio_food_allergies');
        const x_studio_age = params.get('x_studio_age');
        const x_studio_birthdate = params.get('x_studio_birthdate');
        const x_studio_career = params.get('x_studio_career');
        const x_studio_email = params.get('x_studio_email');
        const x_studio_source_1 = params.get('x_studio_source_1');
        const x_studio_weight = params.get('x_studio_weight');
        const x_studio_height = params.get('x_studio_height');
        const x_studio_class = params.get('x_studio_class');

        if (isFromOdoo) {
            if (!branchHN) {
                setPatientContext(prev => ({ ...prev, validationError: 'Missing Branch HN.' }));
                return;
            }
            if (!name) {
                setPatientContext(prev => ({ ...prev, validationError: 'Missing patient name.' }));
                return;
            }
        } else {
            if (patientContext.validationError) {
                setPatientContext(prev => ({ ...prev, validationError: null }));
            }
        }
        
        const context = {
            branchHN, name: decodeURIComponent(name), branch: branch || 'Unknown',
            branchCode, 
            doctor: (doctor && !doctor.startsWith('VC') && !doctor.includes('V Clinic')) ? decodeURIComponent(doctor) : '-',
            centralHN: centralHN || null,
            appointmentDate: appointmentDate || null, mode: mode || 'normal', validationError: null,
            x_studio_personal_illness: decodeURIComponent(x_studio_personal_illness || ''),
            x_studio_drug_allergies: decodeURIComponent(x_studio_drug_allergies || ''),
            x_studio_food_allergies: decodeURIComponent(x_studio_food_allergies || ''),
            x_studio_age: x_studio_age || '',
            x_studio_weight: x_studio_weight || '',
            x_studio_height: x_studio_height || '',
            x_studio_birthdate: decodeURIComponent(x_studio_birthdate || ''),
            x_studio_career: decodeURIComponent(x_studio_career || ''),
            x_studio_email: decodeURIComponent(x_studio_email || ''),
            x_studio_source_1: decodeURIComponent(x_studio_source_1 || ''),
            x_studio_class: decodeURIComponent(x_studio_class || '')
        };
        
        setPatientContext(context);
        if (context.branchHN && context.name) {
            upsertPatient(context); 
        } else {
            setIsPatientSaved(false);
        }
    }, []);

    useEffect(() => {
        if (patientContext.branchHN && !patientContext.validationError && isPatientSaved) {
            // 🔍 Check if this specific tab already carries a layout identity hash
            const currentHash = window.location.hash;
            
            if (currentHash && currentHash.startsWith('#template=')) {
                const savedTemplateName = decodeURIComponent(currentHash.replace('#template=', ''));
                const todayDate = getTodayDate();
                
                console.log(`⚡ [Tab Safe Recovery] Restoring tab access to template: ${savedTemplateName}`);
                
                // Auto-trigger loading behavior directly for this explicit form type
                handleLoadVisit({ visit_date: todayDate, template_name: savedTemplateName });
                setShowVisitHistory(false);
            } else {
                // No hash found (First click from Odoo) -> Proceed to standard modal workflow safely
                setShowVisitHistory(true);
            }
        }
    }, [patientContext.branchHN, isPatientSaved]);

    const extractBranchCode = (branchHN, branchName) => {
        const str = (branchName || "").toString().toLowerCase();
        
        if (str.includes('central') || str.includes('เซ็นทรัล')) return 'CCR';
        if (str.includes('bangna') || str.includes('บางนา')) return 'BN';
        if (str.includes('emporium') || str.includes('เอ็มโพเรียม')) return 'EM';
        if (str.includes('thonglor') || str.includes('ทองหล่อ')) return 'TL';
        if (str.includes('chiangrai') || str.includes('เชียงราย')) return 'CR';
        
        // แผนสำรอง: ถ้าไม่มีชื่อส่งมา ให้ถอยกลับไปตัดตัวหนังสือจาก HN ตามเดิม
        if (!branchHN) return 'UNK';
        const match = branchHN.match(/^([A-Z]+)/);
        return match ? match[1].toUpperCase() : 'UNK';
    };

    const upsertPatient = async (context) => {
        try {
            // 🛡️ ANTI-BLANK SHIELD: แปลงข้อความว่างเปล่า "" ให้กลายเป็น null อย่างเด็ดขาด 
            // เพื่อป้องกันไม่ให้ชนข้อกำหนด UNIQUE Constraint ของฐานข้อมูลหลัก
            const cleanCentralHN = context.centralHN && context.centralHN.trim() !== "" 
                ? context.centralHN.trim() 
                : null;

            await axios.post('/api/patient/upsert', {
                branch_hn: context.branchHN ? context.branchHN.trim() : null, 
                name: context.name ? context.name.trim() : null,
                branch_code: context.branchCode || null, 
                branch_name: context.branch || null,
                central_hn: cleanCentralHN, // 🚀 ส่งค่า null แท้ไปแทนตัวหนังสือว่างเปล่า
                x_studio_personal_illness: context.x_studio_personal_illness || null,
                x_studio_drug_allergies: context.x_studio_drug_allergies || null,
                x_studio_food_allergies: context.x_studio_food_allergies || null,
                x_studio_age: context.x_studio_age || null,
                x_studio_birthdate: context.x_studio_birthdate || null,
                x_studio_career: context.x_studio_career || null,
                x_studio_email: context.x_studio_email || null,
                x_studio_source_1: context.x_studio_source_1 || null,
                x_studio_weight: context.x_studio_weight || null,
                x_studio_height: context.x_studio_height || null
            });
            
            setIsPatientSaved(true);
        } catch (error) { 
            console.error('❌ Error upserting patient:', error);
            setPatientContext(prev => ({
                ...prev,
                validationError: 'Failed to create patient record. Please check console.'
            }));
        }
    };

    useEffect(() => {
        // 🛑 ป้องกันเออร์เรอร์: ถ้ายังไม่มี HN คนไข้ หรือยังไม่ได้เลือกชื่อหมอ/สตาฟ ไม่ต้องรัน
        if (!patientContext.branchHN || !doctorName) return;

        const fetchLockStatus = async () => {
            try {
                const token = localStorage.getItem('session_token') || '';
                const response = await axios.get(`/api/visit/lock-status/${patientContext.branchHN}?editor=${encodeURIComponent(doctorName)}&token=${token}`);
                
                if (response.data.locked) {
                    // 🔒 มีเครื่องอื่นถือสิทธิ์อยู่ -> สั่งล็อกปุ่มเซฟทันที!
                    setIsVisitLocked(true); 
                    setLockMessage(`Editing by: ${response.data.editor} (บน iPad เครื่องอื่น)`);
                } else {
                    // 🟢 แถมพิเศษ: ถ้าเครื่องแรกกดปิดจอหนีไปแล้ว เครื่องเราต้องปลดล็อกออโต้ทันที ไม่ขังลืม
                    setIsVisitLocked(false);
                    setLockMessage('');
                }
            } catch (err) {
                console.error("❌ Lock check failed:", err);
            }
        };

        // 🎯 1. ยิงเช็คครั้งแรกสุดทันทีที่เปิดหน้าจอคนไข้รายนี้
        fetchLockStatus(); 

        // ⏱️ 2. รันระบบเรดาร์ (Polling/Heartbeat) ยิงเช็คซ้ำๆ ทุกๆ 3 วินาที (3000ms)
        // หลังบ้านดึงค่าจาก RAM ตรงๆ ไม่สะเทือนคอขวดแน่นอนครับ
        const lockInterval = setInterval(fetchLockStatus, 3000); 

        // 🧹 3. เคลียร์ RAM หน้าบ้าน: ทำลายนาฬิกาจับเวลาทิ้งทันทีเมื่อสตาฟปิดหน้าจอนี้ หรือสลับคนไข้
        return () => clearInterval(lockInterval);
        
    }, [patientContext.branchHN, doctorName]);

    useEffect(() => { fetchTemplates(); }, []);
    const fetchTemplates = async () => {
        try {
            const response = await axios.get('/api/templates/list');
            if (response.data.success) {
                const filtered = response.data.templates.filter(t => 
                    t.type === 'image' && !t.displayName.startsWith('+')
                );
                setTemplates(filtered);
            }
        } catch (error) { console.error('Error fetching templates:', error); }
    };

    // --- [1. CANVAS INITIALIZATION] ---
    useEffect(() => {
        if (!patientContext.branchHN || fabricCanvasRef.current || isPdfView) return;

        const canvas = new fabric.Canvas(canvasRef.current, {
            width: 800, height: 1000, backgroundColor: '#ffffff',
            isDrawingMode: false, 
            selection: false, 
            enablePointerEvents: true 
        });

        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
        canvas.freeDrawingBrush.color = penColorRef.current;
        canvas.freeDrawingBrush.width = penSizeRef.current;
        canvas.freeDrawingBrush.decimate = 1.5; 

        const handleInputGatekeeper = (e) => {
            const isStylus = e.pointerType === 'pen' || 
                             e.touchType === 'stylus' || 
                             (e.touches && Array.from(e.touches).some(t => t.touchType === 'stylus'));

            if (e.touches && e.touches.length > 1) {
                canvas.isDrawingMode = false;
                isCanvasLockedRef.current = true;
                return;
            }

            if (e.cancelable && (e.type === 'touchstart' || e.type === 'pointerdown')) {
                e.preventDefault(); 
            }

            if (isStylus && !isCanvasLockedRef.current && !isHandToolRef.current && !isReadOnlyRef.current) {
                canvas.isDrawingMode = true;
            } else {
                canvas.isDrawingMode = false;
            }
        };

        const handleInputEnd = () => {
             if (canvas.freeDrawingBrush) canvas.freeDrawingBrush.onMouseUp(); 
        };

        const upperCanvasEl = canvas.upperCanvasEl;
        upperCanvasEl.addEventListener('pointerdown', handleInputGatekeeper, { capture: true, passive: false });
        upperCanvasEl.addEventListener('touchstart', handleInputGatekeeper, { capture: true, passive: false });
        upperCanvasEl.addEventListener('touchend', handleInputEnd);
        upperCanvasEl.addEventListener('dblclick', (e) => e.preventDefault(), { capture: true });

        setupZoomAndPan(canvas);
        fabricCanvasRef.current = canvas;
        
        return () => {
            if (fabricCanvasRef.current) {
                fabricCanvasRef.current.dispose();
                fabricCanvasRef.current = null;
            }
        };
    }, [patientContext.branchHN, isPdfView]);

    useEffect(() => {
        // 🔍 รองรับทั้งคีย์ของ Frontdesk และ Doctor
        const userId = localStorage.getItem('user_id') || localStorage.getItem('doctor_id'); 
        const role = localStorage.getItem('user_role') || 'frontdesk';
        
        // 🛡️ 1. ระบบสั่งเสีย (Beacon) - ทำงานจังหวะสุดท้ายที่สตาฟกดกากบาทปิดแท็บ
        const handleTabClose = () => {
            if (userId) {
                const payload = JSON.stringify({ user_id: userId, role: role });
                navigator.sendBeacon('/api/odoo/portal-logout-flush', payload);
            }
        };
        window.addEventListener('beforeunload', handleTabClose);

        // 💓 2. ระบบชีพจร (Heartbeat) - ทำงานตอนที่เปิดจอค้างไว้
        let heartbeatInterval;
        if (userId) {
            const sendGlobalHeartbeat = () => {
                axios.post('/api/user/heartbeat', { user_id: userId, role: role })
                     .catch((err) => console.warn("📡 Heartbeat skipped", err.message));
            };
            
            sendGlobalHeartbeat(); // ยิงทักทายเซิร์ฟเวอร์ทันที 1 ครั้ง
            heartbeatInterval = setInterval(sendGlobalHeartbeat, 10000); // ตั้งเวลายิงซ้ำทุก 10 วินาที
        }

        // 🧹 3. ทำลายทิ้ง (Cleanup) - ป้องกันเมมโมรี่บวม
        return () => {
            window.removeEventListener('beforeunload', handleTabClose);
            if (heartbeatInterval) clearInterval(heartbeatInterval);
        };
    }, []);

    useEffect(() => {
        // Record the exact time this canvas was opened
        const tabOpenTime = Date.now();
        const MAX_IDLE_TIME = 15 * 60 * 1000; // 15 minutes maximum lifespan

        const handleAppVisibility = () => {
            if (document.visibilityState === 'visible') {
                const idleTime = Date.now() - tabOpenTime;
                
                // If the tab was asleep/backgrounded for more than 15 minutes, kill it.
                if (idleTime > MAX_IDLE_TIME && patientContext.branchHN) {
                    alert("⚠️ เซสชั่นหมดอายุเนื่องจากเปิดหน้าจอทิ้งไว้นานกว่า 15 นาที ระบบจะรีเฟรชเพื่อความปลอดภัยของข้อมูลคนไข้ครับ");
                    window.location.reload(); 
                }
            }
        };

        document.addEventListener('visibilitychange', handleAppVisibility);
        
        return () => {
            document.removeEventListener('visibilitychange', handleAppVisibility);
        };
    }, [patientContext.branchHN]);

    // --- [ERASER LOGIC] ---
    useEffect(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas || isReadOnly || isPdfView) return;

        if (isEraser) {
            canvas.isDrawingMode = false; 
            canvas.selection = false;
            canvas.defaultCursor = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Ctext y='28' font-size='28'%3E%F0%9F%A7%B9%3C/text%3E%3C/svg%3E") 16 16, auto`;
            canvas.getObjects().forEach(obj => { obj.selectable = false; obj.evented = false; });

            let isErasing = false;
            const eraserRadius = eraserSize;

            const checkErase = (e) => {
                const pointer = canvas.getPointer(e.e);
                const objects = canvas.getObjects();
                objects.forEach(obj => {
                    if (obj.name === 'template') return;
                    const objBounds = obj.getBoundingRect();
                    const isNear = (
                        pointer.x >= objBounds.left - eraserRadius && 
                        pointer.x <= objBounds.left + objBounds.width + eraserRadius &&
                        pointer.y >= objBounds.top - eraserRadius && 
                        pointer.y <= objBounds.top + objBounds.height + eraserRadius
                    );
                    if (isNear) { canvas.remove(obj); canvas.renderAll(); }
                });
            };

            const handleMouseDown = (e) => { isErasing = true; checkErase(e); };
            const handleMouseMove = (e) => { if (isErasing) checkErase(e); };
            const handleMouseUp = () => { isErasing = false; };

            canvas.on('mouse:down', handleMouseDown);
            canvas.on('mouse:move', handleMouseMove);
            canvas.on('mouse:up', handleMouseUp);

            return () => {
                canvas.off('mouse:down', handleMouseDown);
                canvas.off('mouse:move', handleMouseMove);
                canvas.off('mouse:up', handleMouseUp);
                canvas.defaultCursor = 'default';
            };
        }
    }, [isEraser, eraserSize, isReadOnly, isPdfView]); 
    
    // --- [2. TOUCH HANDLERS (ZOOM/PAN)] ---
    useEffect(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas || isPdfView) return; 
        const canvasElement = canvas.getElement();
        const canvasContainer = canvasContainerRef.current;
        
        let initialDistance = 0;
        let initialCenter = { x: 0, y: 0 };
        let isGestureActive = false;

        const handleTouchStart = (e) => {
            const isStylus = e.pointerType === 'pen' || 
                             e.touchType === 'stylus' || 
                             (e.touches && Array.from(e.touches).some(t => t.touchType === 'stylus'));
            if (isStylus) return;

            const touches = e.touches;
            if (touches && touches.length === 2) {
                isGestureActive = true;
                canvas.isDrawingMode = false;
                const touch1 = touches[0];
                const touch2 = touches[1];
                initialDistance = Math.sqrt(Math.pow(touch2.clientX - touch1.clientX, 2) + Math.pow(touch2.clientY - touch1.clientY, 2));
                initialCenter = {
                    x: (touch1.clientX + touch2.clientX) / 2,
                    y: (touch1.clientY + touch2.clientY) / 2
                };
            }
        };

        const handleTouchMove = (e) => {
            const isStylus = e.pointerType === 'pen' || (e.touches && Array.from(e.touches).some(t => t.touchType === 'stylus'));
            if (isStylus) return; 

            if (isGestureActive && e.touches && e.touches.length === 2) {
                const touch1 = e.touches[0];
                const touch2 = e.touches[1];
                const newDistance = Math.sqrt(Math.pow(touch2.clientX - touch1.clientX, 2) + Math.pow(touch2.clientY - touch1.clientY, 2));
                
                if (initialDistance > 0 && newDistance > 0) {
                    let zoom = canvas.getZoom();
                    const scale = newDistance / initialDistance;
                    zoom *= scale;
                    if (zoom > 5) zoom = 5;
                    if (zoom < 0.2) zoom = 0.2; 
                    const currentCenterX = (touch1.clientX + touch2.clientX) / 2;
                    const currentCenterY = (touch1.clientY + touch2.clientY) / 2;
                    const rect = canvasElement.getBoundingClientRect();
                    const zoomPoint = new fabric.Point(currentCenterX - rect.left, currentCenterY - rect.top);
                    canvas.zoomToPoint(zoomPoint, zoom);
                    setZoomLevel(zoom);
                    initialDistance = newDistance;
                }

                const newCenterX = (touch1.clientX + touch2.clientX) / 2;
                const newCenterY = (touch1.clientY + touch2.clientY) / 2;
                const deltaX = newCenterX - initialCenter.x;
                const deltaY = newCenterY - initialCenter.y;

                canvas.relativePan(new fabric.Point(deltaX, deltaY));
                initialCenter = { x: newCenterX, y: newCenterY };

                const vpt = canvas.viewportTransform;
                setPanX(vpt[4]); 
                setPanY(vpt[5]);

                if (e.cancelable) e.preventDefault();
                e.stopPropagation();
            }
        };

        const handleTouchEnd = (e) => {
            if (fabricCanvasRef.current && fabricCanvasRef.current.freeDrawingBrush) {
                try {
                    fabricCanvasRef.current.freeDrawingBrush.onMouseUp(e || { e: {} });
                } catch (err) {
                    console.warn("🎨 Fabric Brush safely interrupted:", err.message);
                }
            }

            if (!e.touches || e.touches.length === 0) {
                isGestureActive = false;
                initialDistance = 0;
                isCanvasLockedRef.current = false; 

                if (fabricCanvasRef.current && fabricCanvasRef.current.freeDrawingBrush) {
                    if (fabricCanvasRef.current.freeDrawingBrush._points) {
                        fabricCanvasRef.current.freeDrawingBrush._points = [];
                    }
                }
            }
        };

        if (canvasContainer) {
            canvasContainer.addEventListener('touchstart', handleTouchStart, { passive: false });
            canvasContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
            canvasContainer.addEventListener('touchend', handleTouchEnd, { passive: false });
        }

        return () => {
            if (canvasContainer) {
                canvasContainer.removeEventListener('touchstart', handleTouchStart);
                canvasContainer.removeEventListener('touchmove', handleTouchMove);
                canvasContainer.removeEventListener('touchend', handleTouchEnd);
            }
        };
    }, [fabricCanvasRef.current, patientContext.branchHN, isPanning, isPdfView]);

    const openPdfInNewWindow = async () => {
        if (!pdfUrl) return;
        const newWindow = window.open('', '_blank');
        if (newWindow) {
            newWindow.document.write('<html><body style="display:flex;align-items:center;justify-content:center;font-family:sans-serif;"><h2>Loading PDF...</h2></body></html>');
        }

        setIsSaving(true); 
        try {
            const response = await axios.get(pdfUrl, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const localUrl = URL.createObjectURL(blob);

            if (newWindow) {
                newWindow.location.href = localUrl;
            } else {
                alert("Please allow popups for this site to view PDFs.");
            }
        } catch (error) {
            console.error("❌ PDF Load Error:", error);
            if (newWindow) newWindow.close(); 
            alert("Failed to load PDF. Check connection or login.");
        } finally {
            setIsSaving(false);
        }
    };

    const setupZoomAndPan = (canvas) => {
        canvas.on('mouse:wheel', (opt) => {
            const delta = opt.e.deltaY;
            let zoom = canvas.getZoom();
            zoom *= 0.999 ** delta;
            if (zoom > 5) zoom = 5;
            if (zoom < 0.1) zoom = 0.1;
            const point = new fabric.Point(opt.e.offsetX, opt.e.offsetY);
            canvas.zoomToPoint(point, zoom);
            setZoomLevel(zoom);
            opt.e.preventDefault(); opt.e.stopPropagation();
        });
        
        let panning = false;
        let lastPosX, lastPosY;
        
        canvas.on('mouse:down', (opt) => {
            const evt = opt.e;
            if (isHandToolRef.current || isReadOnlyRef.current) {
                panning = true;
                lastPosX = evt.clientX;
                lastPosY = evt.clientY;
                canvas.defaultCursor = 'grabbing';
            }
        });
        canvas.on('mouse:move', (opt) => {
            if (panning && (isHandToolRef.current || isReadOnlyRef.current)) {
                const e = opt.e;
                const deltaX = e.clientX - lastPosX;
                const deltaY = e.clientY - lastPosY;
                canvas.relativePan(new fabric.Point(deltaX, deltaY));
                lastPosX = e.clientX;
                lastPosY = e.clientY;
                const vpt = canvas.viewportTransform;
                setPanX(vpt[4]); setPanY(vpt[5]);
            }
        });
        canvas.on('mouse:up', () => {
            if (panning) {
                panning = false;
                canvas.defaultCursor = isHandToolRef.current ? 'grab' : 'default';
            }
        });
    };
    
    const forceHandTool = () => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        canvas.isDrawingMode = false;
        setIsEraser(false);
        setIsHandTool(true); 
        canvas.selection = false;
        canvas.defaultCursor = 'grab'; 
        canvas.getObjects().forEach(obj => { obj.selectable = false; obj.evented = false; });
    };

    const zoomIn = () => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        let zoom = canvas.getZoom();
        zoom *= 1.2; if (zoom > 5) zoom = 5;
        const center = canvas.getCenter();
        canvas.zoomToPoint(new fabric.Point(center.left, center.top), zoom);
        setZoomLevel(zoom);
    };

    const zoomOut = () => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        let zoom = canvas.getZoom();
        zoom /= 1.2; if (zoom < 0.1) zoom = 0.1;
        const center = canvas.getCenter();
        canvas.zoomToPoint(new fabric.Point(center.left, center.top), zoom);
        setZoomLevel(zoom);
    };

    const resetZoom = () => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
        setZoomLevel(1); setPanX(0); setPanY(0);
    };

    const activatePenMode = () => {
        if (isReadOnlyRef.current) return;
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        
        setIsHandTool(false); 
        setIsEraser(false); 
        canvas.selection = false; 
        canvas.defaultCursor = 'default';
        canvas.calcOffset();
        canvas.isDrawingMode = false; 
        
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
        canvas.freeDrawingBrush.color = penColorRef.current;
        canvas.freeDrawingBrush.width = penSizeRef.current;
        canvas.freeDrawingBrush.decimate = 1;
        canvas.freeDrawingCursor = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Ctext y='20' font-size='20'%3E%E2%9C%8F%EF%B8%8F%3C/text%3E%3C/svg%3E") 0 24, auto`;

        canvas.getObjects().forEach(obj => { obj.selectable = false; obj.evented = false; });
    };

    const toggleHandTool = () => {
        if (isReadOnly) { forceHandTool(); return; }
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        
        if (isHandTool) {
            activatePenMode();
        } else {
            canvas.isDrawingMode = false;
            canvas.selection = false;
            canvas.defaultCursor = 'grab';
            setIsHandTool(true); setIsEraser(false);
        }
    };

    const getVisitDate = () => {
        if (patientContext.appointmentDate) return patientContext.appointmentDate;
        const today = new Date();
        return today.toISOString().split('T')[0];
    };
    const getTodayDate = () => getVisitDate();

    const handleNewVisit = () => {
        setCurrentVisit(null); 
        setIsVisitLocked(false); 
        setSelectedTemplate(null);
        setIsPdfView(false); 
        setPdfUrl(null);
        
        const canvas = fabricCanvasRef.current;
        if (canvas) { 
            canvas.clear(); 
            canvas.backgroundColor = '#ffffff'; 
            canvas.renderAll(); 
        }
        
        resetZoom();
        setIsHandTool(true); 
        setIsEraser(false);
        setShowTemplates(true);
    };

    const handleLoadVisit = async (visit) => {
    try {
        let visitDate, templateName;
        
        if (visit.date && visit.template) {
            visitDate = visit.date;
            templateName = visit.template;
        } else if (visit.visit_date && visit.template_name) {
            visitDate = visit.visit_date;
            templateName = visit.template_name;
        } else {
            const visitData = visit.visitData || visit;
            visitDate = visitData.visit_date;
            templateName = visitData.template_name;
        }
        
        if (!visitDate || !templateName) {
            alert('Error: Missing visit date or template name.');
            return;
        }
        
        // 🔥 SYNC ACTIVE HASH: Ensure refreshes reload this explicit document type
        window.location.hash = `template=${encodeURIComponent(templateName)}`;
        
        const response = await axios.get(`/api/patient/${patientContext.branchHN}/visit/${visitDate}/${encodeURIComponent(templateName)}`);
            
            if (response.data.success) {
                const fullVisit = response.data.visit;
                setCurrentVisit(fullVisit);
                
                // 📄 1. PDF CHECK
                const isPdf = fullVisit.image_path && fullVisit.image_path.toLowerCase().endsWith('.pdf');
                if (isPdf) {
                    const filename = fullVisit.image_path.split('/').pop();
                    const constructedUrl = `/api/patient/${patientContext.branchHN}/photo/${filename}`;

                    if (fabricCanvasRef.current) {
                        fabricCanvasRef.current.clear();
                        fabricCanvasRef.current.renderAll();
                    }

                    setPdfUrl(constructedUrl);
                    setIsPdfView(true); 
                                                            
                    fullVisit.photo_url = constructedUrl; 
                    fullVisit.visit_type = 'photo'; 
                    setCurrentVisit(fullVisit); 

                    setIsVisitLocked(true);
                    setSelectedTemplate({ 
                        displayName: fullVisit.template_name, 
                        name: fullVisit.template_name 
                    });
                    return; 
                }

                setIsPdfView(false);
                setPdfUrl(null);

                const canvas = fabricCanvasRef.current;
                if (!canvas) return;
                canvas.clear(); 
                canvas.backgroundColor = '#ffffff';

                // 📸 2. PHOTO LOGIC
                if (fullVisit.visit_type === 'photo' && fullVisit.image_path) {
                    const filename = fullVisit.image_path.split('/').pop();
                    const imageUrl = `/api/patient/${patientContext.branchHN}/photo/${filename}`;
                    
                    fabric.FabricImage.fromURL(imageUrl, { crossOrigin: 'anonymous' }).then((img) => {
                        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
                        img.set({
                            left: canvas.width / 2,
                            top: canvas.height / 2,
                            originX: 'center',
                            originY: 'center',
                            scaleX: scale,
                            scaleY: scale,
                            selectable: false,
                            evented: false,
                            name: 'template'
                        });
                        canvas.add(img);
                        canvas.sendObjectToBack(img);
                        canvas.renderAll();
                    });

                    setIsVisitLocked(true);
                    forceHandTool();
                    setSelectedTemplate({ displayName: fullVisit.template_name, name: fullVisit.template_name });
                    return;
                }

                // ✏️ 3. DIGITAL DRAWING LOGIC
                const isLocked = fullVisit.notes && fullVisit.notes.includes(CONFIRM_LOCK_FLAG);
                setIsVisitLocked(isLocked);

                const template = templates.find(t => t.displayName === fullVisit.template_name);
                if (!template) { 
                    setSelectedTemplate({ displayName: fullVisit.template_name, name: fullVisit.template_name }); 
                } else { 
                    setSelectedTemplate(template); 
                }
                
                if (fullVisit.canvas_data) {
                    let canvasData = fullVisit.canvas_data;
                    if (typeof canvasData === 'string') { 
                        canvasData = JSON.parse(canvasData); 
                    }
                    if (canvasData.background) { 
                        canvas.backgroundColor = canvasData.background; 
                    }
                    
                    if (canvasData.objects && canvasData.objects.length > 0) {
                        fabric.util.enlivenObjects(canvasData.objects).then((enlivenedObjects) => {
                            enlivenedObjects.forEach((obj) => { 
                                canvas.add(obj); 
                            });
                            
                            canvas.getObjects().forEach(obj => {
                                if (obj.type === 'image' || obj.type === 'FabricImage') {
                                    obj.set({ name: 'template', selectable: false, evented: false });
                                    canvas.sendObjectToBack(obj);
                                } else {
                                    obj.set({ selectable: false, evented: false });
                                }
                            });

                            if (isLocked) { forceHandTool(); } else { activatePenMode(); }
                            resetZoom();
                            canvas.renderAll();
                        }).catch(err => console.error("🎨 Objects failed to enliven:", err));

                    } else { 
                        canvas.renderAll(); 
                    }
                }
            }
        } catch (error) { 
            console.error('❌ Load visit error:', error);
            alert('Failed to load visit'); 
        }
    };
    
    const handleConfirmVisit = () => {
        if (isReadOnly || isPdfView) return; 
        const isConfirmed = window.confirm("Are you sure you want to confirm and lock this visit?\n\nYou will NOT be able to edit it again.");
        if (isConfirmed) { saveCanvas({ isConfirming: true }); }
    };

    const saveCanvas = async (options = {}) => {
        if (isPdfView) { alert("Cannot save edits to a PDF."); return; }
        const canvas = fabricCanvasRef.current;
        if (!canvas || !patientContext.branchHN) return;
        if (isReadOnly && !options.isConfirming) { alert('Read-only visit.'); return; }
        if (!selectedTemplate) { alert('Select a template first!'); return; }

        const visitDate = currentVisit?.visit_date || getTodayDate();
        const templateName = selectedTemplate.displayName || selectedTemplate.name;

        setIsSaving(true);
        try {
            if (canvas.isDrawingMode && canvas.freeDrawingBrush) {
                try {
                    canvas.freeDrawingBrush.onMouseUp({ e: {} }); 
                } catch (e) { console.warn("Safety brush commit"); }
            }

            canvas.discardActiveObject();
            canvas.renderAll();

            const canvasData = canvas.toJSON(['name', 'selectable', 'evented']);
            
            if (!canvasData.objects || canvasData.objects.length === 0) {
                console.error("⚠️ Canvas data is empty. Aborting save to prevent overwriting.");
                setIsSaving(false);
                alert("⚠️ ข้อมูลว่างเปล่า กรุณาลองเซฟใหม่อีกครั้ง");
                return;
            }

            const currentNotes = currentVisit?.notes || '';
            let newNotes = currentNotes;
            if (options.isConfirming && !currentNotes.includes(CONFIRM_LOCK_FLAG)) {
                newNotes = (currentNotes.trim() + ' ' + CONFIRM_LOCK_FLAG).trim();
            }

            // 🚀 อัปเกรด BODY: แอบพ่วงเวลาแก้ไขล่าสุดที่เราโหลดมาตอนแรก ยิงส่งไปให้หลังบ้านตรวจสอบ
            const requestBody = { 
                canvas_data: canvasData, 
                doctor_name: patientContext.doctor, 
                notes: newNotes || null,
                visit_type: 'digital',
                appointment_date: patientContext.appointmentDate || null,
                last_updated_at: currentVisit?.updated_at || null // 🔥 คีย์การ์ดตรวจเช็คประวัติเน่า
            };

            let updateUrl;
            if (currentVisit && currentVisit.id) {
                updateUrl = `/api/visit/${currentVisit.id}`; 
            } else {
                updateUrl = `/api/patient/${patientContext.branchHN}/visit/${visitDate}/${encodeURIComponent(templateName)}`;
            }
            
            try {
                const response = await axios.put(updateUrl, requestBody, { timeout: 30000 }); 
                setCurrentVisit(response.data.visit);
                setLastSaved(new Date());
                
                if (options.isConfirming) { 
                    setIsVisitLocked(true); 
                    forceHandTool(); 
                }
            } catch (updateError) {
                // 🛑 INTERCEPT CONFLICT (409): ดักเตะกรณีหน้าร้านเปิดแอปค้างไว้แล้วข้อมูลชนกันเด็ดขาด!
                if (updateError.response?.status === 409) {
                    setIsSaving(false);
                    alert("⚠️ แจ้งเตือนข้อมูลซ้อนทับ (Version Conflict)!\n\nมีเจ้าหน้าที่ท่านอื่นอัปเดตประวัติเคสนี้ไปก่อนหน้านี้แล้ว เพื่อป้องกันข้อมูลบันทึกทับงานคนอื่น ระบบจะทำโหลดประวัติล่าสุดใหม่อีกครั้งโดยอัตโนมัติครับ");
                    
                    // สั่งให้หน้าจอ iPad บังคับรีโหลดดึงภาพวาดล่าสุดจาก DB มาอัปเดตหน้าจอทันที
                    if (currentVisit) {
                        handleLoadVisit(currentVisit);
                    }
                    return; // ดีดออกจากการเซฟทันที ดาต้าเก่าปลอดภัย 100%
                }

                if (updateError.response?.status === 404 || updateError.response?.status === 500) {
                    const createUrl = `/api/patient/${patientContext.branchHN}/visit`;
                    const createBody = { 
                        visit_date: visitDate, 
                        visit_time: new Date().toISOString(), 
                        template_name: templateName,
                        visit_type: 'digital',
                        branch_hn: patientContext.branchHN,
                        branch_code: patientContext.branchCode, 
                        ...requestBody 
                    };
                    const response = await axios.post(createUrl, createBody);
                    setCurrentVisit(response.data.visit);
                    setLastSaved(new Date());

                    if (options.isConfirming) { 
                        setIsVisitLocked(true); 
                        forceHandTool(); 
                    }
                } else { 
                    throw updateError; 
                }
            }
        } catch (error) { 
            console.error('❌ Save error:', error.response?.data || error.message);
            alert('Failed to save: ' + (error.response?.data?.error || error.message)); 
        } 
        finally { setTimeout(() => setIsSaving(false), 500); }
    };

    const clearCanvas = () => {
        if (isReadOnly || isPdfView) return;
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        canvas.getObjects().forEach(obj => { if (obj.name !== 'template') { canvas.remove(obj); } });
        canvas.renderAll();
    };

    const toggleEraser = () => {
        if (isReadOnly) return;
        if (isEraser) {
            activatePenMode(); 
        } else {
            setIsEraser(true);
            setIsHandTool(false);
        }
    };

    const changePenColor = (color) => {
        setPenColor(color); 
        penColorRef.current = color;
        const canvas = fabricCanvasRef.current;
        if (canvas && canvas.freeDrawingBrush && !isEraser) {
             canvas.freeDrawingBrush.color = color;
        }
    };

    const changePenSize = (size) => {
        setPenSize(size); 
        penSizeRef.current = size;
        const canvas = fabricCanvasRef.current;
        if (canvas && canvas.freeDrawingBrush && !isEraser) {
             canvas.freeDrawingBrush.width = size;
        }
    };

    const loadTemplate = async (template) => {
        if (isReadOnly) return;
        setIsPdfView(false);
        setPdfUrl(null);
        
        const canvas = fabricCanvasRef.current;
        if (!canvas) return; 

        try {
            canvas.clear(); canvas.backgroundColor = '#ffffff'; resetZoom();
            const img = await fabric.FabricImage.fromURL(template.url);
            const scaleX = canvas.width / img.width;
            const scaleY = canvas.height / img.height;
            const scale = Math.min(scaleX, scaleY);
            const left = (canvas.width - (img.width * scale)) / 2;
            const top = (canvas.height - (img.height * scale)) / 2;

            img.set({ name: 'template', scaleX: scale, scaleY: scale, left: left, top: top, selectable: false, evented: false });
            canvas.add(img); canvas.sendObjectToBack(img); canvas.renderAll();
            
            // 🔥 LOCK THIS TAB: Write the template type directly into the frontend URL bar hash
            const tName = template.displayName || template.name;
            window.location.hash = `template=${encodeURIComponent(tName)}`;

            activatePenMode(); 
            setSelectedTemplate(template); setShowTemplates(false); setCurrentVisit(null); setIsVisitLocked(false); setIsHandTool(true); setIsEraser(false);
        } catch (error) { alert('Failed to load template: ' + error.message); }
    };

    const exportAsPNG = () => {
        if (isPdfView) { alert("Cannot export PDF as PNG here."); return; }
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const currentTransform = canvas.viewportTransform.slice();
        const currentZoom = zoomLevel; const currentPanX = panX; const currentPanY = panY;
        resetZoom(); canvas.renderAll();
        setTimeout(() => {
            const dataURL = canvas.toDataURL({ format: 'png', quality: 1.0, multiplier: 1.5 });
            const link = document.createElement('a');
            link.download = `OPD_${patientContext.branchHN}_${getTodayDate()}_${selectedTemplate?.displayName || 'visit'}.png`;
            link.href = dataURL;
            document.body.appendChild(link); link.click(); document.body.removeChild(link);
            canvas.setViewportTransform(currentTransform); setZoomLevel(currentZoom); setPanX(currentPanX); setPanY(currentPanY); canvas.renderAll();
        }, 100);
    };
    
    const exportAsPDF = () => {
        if (isPdfView) { window.open(pdfUrl, '_blank'); return; }
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const currentTransform = canvas.viewportTransform.slice();
        const currentZoom = zoomLevel; const currentPanX = panX; const currentPanY = panY;
        resetZoom(); canvas.renderAll();
        setTimeout(() => {
            const dataURL = canvas.toDataURL({ format: 'jpeg', quality: 0.9, multiplier: 1.5 });
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth(); const pdfHeight = pdf.internal.pageSize.getHeight();
            pdf.addImage(dataURL, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`OPD_${patientContext.branchHN}_${getTodayDate()}_${selectedTemplate?.displayName || 'visit'}.pdf`);
            canvas.setViewportTransform(currentTransform); setZoomLevel(currentZoom); setPanX(currentPanX); setPanY(currentPanY); canvas.renderAll();
        }, 100);
    };

    const floatingToolbarStyle = {
        position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
        backgroundColor: '#ffffff', borderRadius: '50px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1001, padding: '12px 20px', display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center', transition: 'all 0.3s ease'
    };
    
    const toolButtonBase = {
        width: '48px', height: '48px', borderRadius: '50%', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', transition: 'all 0.2s ease',
    };
    
    const getToolButtonStyle = (isActive, isDisabled, activeBg = '#333333', defaultBg = '#f4f4f4') => ({
        ...toolButtonBase,
        backgroundColor: isDisabled ? '#cccccc' : (isActive ? activeBg : defaultBg),
        color: isDisabled ? '#888888' : (isActive ? '#ffffff' : '#333333'),
        cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.6 : 1,
    });

    const penSizeOptions = [ { value: 0.7, visualHeight: 3 }, { value: 1.3, visualHeight: 8 } ];
    const penColorOptions = [
        { value: '#000000', border: '#555555' }, { value: '#FFFFFF', border: '#DDDDDD' }, { value: '#D32F2F', border: '#9A0007' },
        { value: '#0066cc', border: '#003d7a' }, { value: '#046f2dff', border: '#1B5E20' }, { value: '#673AB7', border: '#311B92' },
        { value: '#F57C00', border: '#E65100' }, { value: '#ca048bff', border: '#880E4F' },
    ];

    const handlePortalLogout = async () => {
        try {
            const userId = localStorage.getItem('doctor_id');
            const token = localStorage.getItem('session_token');
            const role = localStorage.getItem('user_role'); // 🔑 NEW: Grab their role ('doctor' or 'frontdesk')

            if (userId && token) {
                // Pass the role to the backend so it knows which table to wipe
                await axios.post('/api/odoo/portal-logout', { 
                    user_id: userId, 
                    session_token: token,
                    role: role 
                });
            }
        } catch (e) {
            console.error("Token release failed on logout", e);
        } finally {
            localStorage.clear();
            window.location.reload();
        }
    };

    if (!isFromOdoo) {
        if (!doctorName) {
            return <PortalGate onLoginSuccess={() => window.location.reload()} />;
        }
        
        if (userRole === 'frontdesk') {
            return (
                <FrontDeskDashboard 
                    onLogout={handlePortalLogout}
                    onSelectPatient={(p) => {
                        const query = new URLSearchParams({
                            central_hn: p.central_hn || '',
                            branch_hn: p.branch_hn || '',
                            name: p.name || '', 
                            branch: userBranch || '',
                            doctor: p.doctor || '',
                            x_studio_personal_illness: p.x_studio_personal_illness || '',
                            x_studio_drug_allergies: p.x_studio_drug_allergies || '',
                            x_studio_food_allergies: p.x_studio_food_allergies || '',
                            x_studio_height: p.x_studio_height || '',
                            x_studio_weight: p.x_studio_weight || '',
                            x_studio_class: p.x_studio_class || '',
                            // 🚀 เติมท่อส่งแถวที่ 3 และกล่องนัดหมายให้สมบูรณ์
                            appointment_date: p.appointment_date || '',
                            x_studio_age: p.x_studio_age || '',
                            x_studio_birthdate: p.x_studio_birthdate || '',
                            x_studio_career: p.x_studio_career || '',
                            x_studio_source_1: p.x_studio_source_1 || '',
                            x_studio_email: p.x_studio_email || ''
                        });
                        window.location.search = `?${query.toString()}`;
                    }}
                />
            );
        }

        return (
            <DoctorDashboard 
                onLogout={handlePortalLogout}
                onSelectPatient={(p) => {
                    const query = new URLSearchParams({
                        central_hn: p.central_hn || '',
                        branch_hn: p.branch_hn || '',
                        name: p.name || '', 
                        branch: userBranch || '',
                        doctor: p.doctor ||  '',
                        x_studio_personal_illness: p.x_studio_personal_illness || '',
                        x_studio_drug_allergies: p.x_studio_drug_allergies || '',
                        x_studio_food_allergies: p.x_studio_food_allergies || '',
                        x_studio_height: p.x_studio_height || '',
                        x_studio_weight: p.x_studio_weight || '',
                        x_studio_class: p.x_studio_class || '',
                        // 🚀 เติมท่อส่งฝั่งคุณหมอด้วยเช่นกันครับกัปตัน
                        appointment_date: p.appointment_date || '',
                        x_studio_age: p.x_studio_age || '',
                        x_studio_birthdate: p.x_studio_birthdate || '',
                        x_studio_career: p.x_studio_career || '',
                        x_studio_source_1: p.x_studio_source_1 || '',
                        x_studio_email: p.x_studio_email || ''
                    });
                    window.location.search = `?${query.toString()}`;
                }}
            />
        );
    }

    return (
        <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '1200px', margin: '0 auto' }}>
            <VisitHistoryModal
                isOpen={showVisitHistory} onClose={() => setShowVisitHistory(false)}
                patientContext={patientContext} onNewVisit={handleNewVisit} onLoadVisit={handleLoadVisit}
            />

            {isReadOnly && lockMessage && (
                <div style={{
                    backgroundColor: '#fff3cd', 
                    color: '#856404', 
                    padding: '12px', 
                    textAlign: 'center', 
                    borderRadius: '8px',
                    marginBottom: '15px',
                    border: '1px solid #ffeeba',
                    fontWeight: 'bold'
                }}>
                    ⚠️ {lockMessage} — Save and Confirm are disabled.
                </div>
            )}

            {!patientContext.validationError && patientContext.branchHN && !isReadOnly && (
                !isPdfView && (
                <div style={floatingToolbarStyle}>
                    <button 
                        onClick={toggleEraser} disabled={isReadOnly}
                        style={getToolButtonStyle(isEraser, isReadOnly, '#dc3545', '#ffffff')}
                        title={isEraser ? "Switch to Pen" : "Switch to Eraser"}
                    >
                        {isEraser ? <LuPenLine size={24} /> : <LuEraser size={24} />}
                    </button>

                    <div style={{ width: '2px', backgroundColor: '#e0e0e0', height: '40px' }} />

                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <button
                            onClick={() => setShowFileOps(!showFileOps)}
                            style={getToolButtonStyle(showFileOps, false, '#555555', '#009688')}
                            title="File Options"
                        >
                            {showFileOps ? <LuX size={24} /> : <LuEllipsisVertical size={24} />}
                        </button>

                        {showFileOps && (
                            <div style={{
                                position: 'absolute', bottom: '60px', left: '50%', transform: 'translateX(-50%)',
                                display: 'flex', flexDirection: 'row', gap: '10px', backgroundColor: '#ffffff',
                                padding: '10px', borderRadius: '50px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                            }}>
                                <button 
                                    onClick={() => saveCanvas()} disabled={isSaving || !selectedTemplate || isReadOnly}
                                    style={getToolButtonStyle(false, isSaving || !selectedTemplate || isReadOnly, '#28a745', '#28a745')}
                                    title="Save"
                                >
                                    {isSaving ? '⏳...' : <LuSave size={22} />}
                                </button>
                                
                                <button onClick={exportAsPNG} style={getToolButtonStyle(false, false, '#17a2b8', '#17a2b8')} title="Export as PNG"> <LuImage size={22} /> </button>
                                <button onClick={exportAsPDF} style={getToolButtonStyle(false, false, '#E91E63', '#E91E63')} title="Export as PDF"> <LuFileText size={22} /> </button>
                            </div>
                        )}
                    </div>
                    
                    {(!isEraser && !isReadOnly && !isHandTool) && (
                        <div style={{ width: '2px', backgroundColor: '#e0e0e0', height: '40px' }} />
                    )}

                    {!isEraser && !isReadOnly && !isHandTool && (
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                            {penColorOptions.map(opt => (
                                <button 
                                    key={opt.value} onClick={() => changePenColor(opt.value)}
                                    style={{
                                        width: '32px', height: '32px', backgroundColor: opt.value,
                                        border: penColor === opt.value ? `3px solid ${opt.border}` : `2px solid ${opt.value}`,
                                        borderRadius: '50%', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                    title={`Color: ${opt.value}`}
                                />
                            ))}
                            
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                    onClick={() => setShowColorPicker(!showColorPicker)}
                                    style={{
                                        width: '32px', height: '32px',
                                        background: 'linear-gradient(135deg, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)',
                                        border: showColorPicker ? '3px solid #2196F3' : '2px solid #ddd',
                                        borderRadius: '50%', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}
                                    title="More colors"
                                >
                                    <span style={{ fontSize: '16px', filter: 'drop-shadow(0 0 1px white) drop-shadow(0 0 1px white)' }}>🎨</span>
                                </button>
                                
                                {showColorPicker && (
                                    <div style={{
                                        position: 'absolute', bottom: '50px', left: '50%', transform: 'translateX(-50%)',
                                        backgroundColor: '#ffffff', padding: '15px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                                        display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px', zIndex: 2000, minWidth: '220px'
                                    }}>
                                        {[
                                            '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
                                            '#8B0000', '#DC143C', '#FF0000', '#FF6347', '#FFA07A', '#FFB6C1',
                                            '#FF4500', '#FF8C00', '#FFA500', '#FFD700', '#FFFF00', '#FFFFE0',
                                            '#006400', '#228B22', '#00FF00', '#32CD32', '#90EE90', '#98FB98',
                                            '#00008B', '#0000FF', '#4169E1', '#1E90FF', '#87CEEB', '#ADD8E6',
                                            '#4B0082', '#8B008B', '#9370DB', '#BA55D3', '#DDA0DD', '#EE82EE',
                                            '#8B4513', '#A0522D', '#CD853F', '#D2691E', '#F4A460', '#DEB887'
                                        ].map((color) => (
                                            <button
                                                key={color} onClick={() => { changePenColor(color); setShowColorPicker(false); }}
                                                style={{
                                                    width: '30px', height: '30px', backgroundColor: color,
                                                    border: penColor === color ? '3px solid #2196F3' : '1px solid #ddd',
                                                    borderRadius: '6px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                }}
                                                title={color}
                                            />
                                        ))}
                                        <button
                                            onClick={() => setShowColorPicker(false)}
                                            style={{
                                                gridColumn: 'span 6', marginTop: '8px', padding: '8px', backgroundColor: '#f5f5f5',
                                                border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold'
                                            }}
                                        >
                                            ✖ Close
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {!isEraser && !isReadOnly && !isHandTool && (
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                            {penSizeOptions.map(opt => (
                                <button key={opt.value}
                                    onClick={() => changePenSize(opt.value)}
                                    style={{
                                        width: '48px', height: '32px', backgroundColor: '#f5f5f5',
                                        border: penSize === opt.value ? '3px solid #9c27b0' : '2px solid #cccccc',
                                        borderRadius: '16px', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}
                                    title={`Pen size: ${opt.value}`}
                                >
                                    <div style={{ width: '30px', height: `${opt.visualHeight}px`, backgroundColor: penColor, borderRadius: `${opt.visualHeight/2}px` }} />
                                </button>
                            ))}
                        </div>
                    )}

                    {isEraser && !isReadOnly && (
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                            {[ { size: 6, label: 'S' }, { size: 10, label: 'M' }, { size: 15, label: 'L' } ].map(opt => (
                                <button 
                                    key={opt.size} onClick={() => setEraserSize(opt.size)}
                                    style={{
                                        width: '48px', height: '32px', backgroundColor: '#f5f5f5',
                                        border: eraserSize === opt.size ? '3px solid #dc3545' : '2px solid #cccccc',
                                        borderRadius: '16px', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', color: '#666'
                                    }}
                                    title={`Eraser size: ${opt.size}px`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                )
            )}

            {patientContext.validationError && (
                <div style={{
                    padding: '20px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '8px',
                    marginBottom: '20px', border: '2px solid #ef5350', fontSize: '18px', fontWeight: 'bold', textAlign: 'center'
                }}>
                    ⚠️ {patientContext.validationError}
                    <div style={{ marginTop: '10px', fontSize: '14px', fontWeight: 'normal' }}> Please open this system from Odoo patient record. </div>
                </div>
            )}

            {!patientContext.validationError && patientContext.branchHN && (
                <>
                    <CollapsiblePatientInfo 
                        patientContext={patientContext} currentVisit={currentVisit} selectedTemplate={selectedTemplate}
                        templates={templates} isReadOnly={isReadOnly} isSaving={isSaving} lastSaved={lastSaved}
                        showTemplates={showTemplates} onShowVisitHistory={() => setShowVisitHistory(true)}
                        onShowTemplates={() => setShowTemplates(!showTemplates)} onLoadTemplate={loadTemplate}
                        onClearAll={clearCanvas} onConfirmVisit={handleConfirmVisit} onExportPNG={exportAsPNG} onExportPDF={exportAsPDF}
                        zoomLevel={Math.round(zoomLevel * 100)} onZoomIn={zoomIn} onZoomOut={zoomOut} onResetZoom={resetZoom}
                    />

                    {/* 🆕 MAIN VIEW CONTAINER */}
                    <div 
                        ref={canvasContainerRef}
                        style={{ 
                            border: '3px solid #333', 
                            borderRadius: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center', 
                            width: '100%',
                            minHeight: '80vh', 
                            backgroundColor: '#fff',
                            position: 'relative',
                            overflow: isPdfView ? 'auto' : 'hidden',
                            WebkitOverflowScrolling: 'touch',
                            touchAction: isPdfView ? 'auto' : 'none',
                            WebkitUserSelect: 'none',
                            userSelect: 'none',
                            WebkitTouchCallout: 'none'
                        }}
                    >
                        {/* 📄 PDF LAYER: Only visible when isPdfView is true */}
                        <div style={{ 
                            display: isPdfView ? 'flex' : 'none', 
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: '40px',
                            textAlign: 'center'
                        }}>
                            <LuFileText size={80} color="#dc3545" style={{ marginBottom: '20px' }} />
                            <h2 style={{ marginBottom: '10px' }}>PDF Document Loaded</h2>
                            <p style={{ color: '#666', marginBottom: '25px' }}>Multi-page scrolling is available in the full viewer.</p>
                            
                            <button 
                                onClick={openPdfInNewWindow}
                                disabled={isSaving}
                                style={{
                                    padding: '16px 32px',
                                    backgroundColor: isSaving ? '#ccc' : '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50px',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    cursor: isSaving ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isSaving ? '⏳ Loading...' : '📂 Open PDF Full Screen'}
                            </button>
                        </div>

                        {/* 🎨 CANVAS LAYER: ALWAYS in the DOM, just hidden during PDF view */}
                        <div style={{ 
                            display: isPdfView ? 'none' : 'flex', 
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center', 
                            alignItems: 'center'                            
                        }}>
                            <canvas ref={canvasRef} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default App;